import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { ShieldCheck, Loader2, AlertCircle, Link2, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { PdfPreviewViewer } from '../../ui/PdfPreviewViewer';

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_BADGE = {
  assignment: { label: 'Linked Assignment', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  legacy_fallback: { label: 'Legacy Directory Fallback', cls: 'bg-amber-50 text-amber-800 border-amber-300' },
  none: { label: 'No Collector Data', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

/**
 * Review Tax Certification modal — TAXREVIEW-UI-001.
 *
 * Visuals mirror the Typing Final Report Review modal (shared review
 * pattern): TitleFlow blue header w/ white title + X, light info panel,
 * bordered PDF container, amber acknowledgement strip, slate footer.
 * Tax content/business logic unchanged (same preview + tax-sheet-review
 * endpoints, collector-source badges, PdfPreviewViewer).
 *
 * Flicker fix (Confirm Review): previously cleanup() cleared pdfData/pdfUrl
 * BEFORE onOpenChange(false), unmounting the PDF viewer and flashing the
 * empty-state while the modal was still visible; the close-effect cleanup
 * then cleared it again during the dialog close animation. Now the modal
 * closes FIRST — onOpenChange(false) + onReviewComplete run in the same
 * synchronous tick (one batched render: modal gone + parent updated). PDF
 * state is never cleared while visible; stale object URLs are revoked on
 * the next fetch / unmount only.
 */
const ReviewTaxCertModal = ({ open, onOpenChange, orderId, onReviewComplete }) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfData, setPdfData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sources, setSources] = useState(null);
  const [narrativeStatus, setNarrativeStatus] = useState(null);
  const urlRef = useRef('');

  // TAXCERT-TIER3-FALLBACK-001 — surface parcels without a reviewed narrative
  const fetchNarrativeStatus = useCallback(async () => {
    setNarrativeStatus(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/api/orders/${orderId}/tax-cert/narrative-status`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) setNarrativeStatus(await res.json());
    } catch {
      // Non-blocking: the banner simply doesn't render if the check fails
    }
  }, [orderId]);

  const fetchSources = useCallback(async () => {
    setSources(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/api/orders/${orderId}/tax-cert/collector-sources`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) setSources(await res.json());
    } catch {
      // Non-blocking: badges simply don't render if the summary fails
    }
  }, [orderId]);

  const fetchPdf = useCallback(async () => {
    setPdfLoading(true);
    setPdfData(null);
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = ''; }
    setPdfUrl('');
    setAcknowledged(false);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API}/api/orders/${orderId}/tax-cert/preview-pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const arrayBuffer = await response.arrayBuffer();
        setPdfData(new Uint8Array(arrayBuffer));
        const url = URL.createObjectURL(new Blob([arrayBuffer], { type: 'application/pdf' }));
        urlRef.current = url;
        setPdfUrl(url);
      } else {
        toast.error('Could not generate PDF preview. You can still acknowledge the review.');
      }
    } catch {
      toast.error('Failed to load PDF preview.');
    } finally {
      setPdfLoading(false);
    }
  }, [orderId]);

  const submitReview = useCallback(async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API}/api/orders/${orderId}/tax-sheet-review`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (response.ok) {
        const result = await response.json();
        // Close + parent update in ONE synchronous tick (batched render).
        onOpenChange(false);
        onReviewComplete({
          status: result.review_status,
          reviewed_by: result.reviewed_by,
          reviewed_by_name: result.reviewed_by_name,
          reviewed_at: result.reviewed_at
        });
        toast.success('Tax sheet reviewed and acknowledged');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to mark tax sheet as reviewed');
      }
    } catch (error) {
      console.error('Error reviewing tax sheet:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [orderId, onReviewComplete, onOpenChange]);

  useEffect(() => {
    if (open) {
      fetchPdf();
      fetchSources();
      fetchNarrativeStatus();
    }
    // No cleanup on close — clearing PDF state while the modal is (or was
    // just) visible is what caused the flicker. Next open re-fetches fresh.
  }, [open]); // eslint-disable-line

  // Revoke any outstanding object URL on unmount only.
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  // Memoized viewer element — unrelated modal state changes (acknowledged /
  // submitting) must never re-render the PDF canvas mid-confirm.
  const viewerEl = useMemo(() => (
    (pdfData || pdfUrl)
      ? <PdfPreviewViewer pdfData={pdfData} fallbackUrl={pdfUrl} className="min-h-[500px]" />
      : null
  ), [pdfData, pdfUrl]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="review-tax-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* ── TitleFlow blue header (matches Final Report Review) ── */}
        <div className="flex items-center justify-between px-5 py-3.5 text-white" style={{ background: '#004aad' }}>
          <h3 className="text-[15px] font-semibold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Review Tax Certification
          </h3>
          <button onClick={() => onOpenChange(false)} data-testid="review-tax-close-btn" className="p-1 rounded hover:bg-white/15"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="text-[12.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-testid="review-tax-info-note">
            Review the generated <span className="font-semibold">Tax Certificate</span> preview below before marking the
            sheet as reviewed.
          </div>

          {narrativeStatus?.missing?.length > 0 && (
            <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3" data-testid="review-missing-narrative-warning">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-[12.5px] text-amber-900">
                <p className="font-semibold">
                  No reviewed tax narrative for {narrativeStatus.missing.map(m => m.label).join(', ')}
                </p>
                <p className="mt-0.5">
                  The NOTES section below has no tax narrative for {narrativeStatus.missing.length > 1 ? 'these parcels' : 'this parcel'}.
                  Open the Tax Summary for the parcel and re-save (edit any field or reopen this review) so the Combined Tax
                  Certification Notes are generated. Complete and QC Approve are blocked until then.
                </p>
              </div>
            </div>
          )}

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
              <FileText className="h-4 w-4 text-slate-500" />
              <span className="text-[13px] font-semibold text-slate-700">Tax Certificate</span>
              <span className="text-[11px] text-slate-400 ml-auto">Generated from Tax Fulfillment data</span>
            </div>
            {pdfLoading ? (
              <div className="h-64 flex items-center justify-center text-slate-400 text-sm" data-testid="review-pdf-loading">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Generating PDF preview…
              </div>
            ) : viewerEl ? (
              viewerEl
            ) : (
              <div className="p-8 text-center">
                <AlertCircle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-600">PDF preview could not be loaded.</p>
                <p className="text-xs text-slate-400 mt-1">You can still acknowledge the review below.</p>
              </div>
            )}
          </div>

          {/* Staff-only collector source badges (client PDF unchanged) */}
          {sources?.jurisdictions?.length > 0 && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5" data-testid="collector-source-summary">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <Link2 className="h-3 w-3" /> Collector Source (staff only)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {sources.jurisdictions.map((j, idx) => {
                  const cur = SOURCE_BADGE[j.current_source] || SOURCE_BADGE.none;
                  return (
                    <span
                      key={`${j.jurisdiction_id || idx}`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium ${cur.cls}`}
                      title={`Current: ${j.current_source} · Delinquent: ${j.delinquent_source}`}
                      data-testid={`collector-source-badge-${idx}`}
                    >
                      {j.jurisdiction_name || 'Jurisdiction'} · {cur.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <label className="flex items-start gap-2.5 cursor-pointer select-none bg-amber-50/60 border border-amber-200 rounded-lg px-4 py-3" data-testid="review-acknowledge-label">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={e => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              data-testid="review-acknowledge-checkbox"
            />
            <span className="text-[12.5px] text-slate-700 font-medium">
              I have reviewed and confirm the tax certification is accurate.
            </span>
          </label>
        </div>

        {/* ── Footer (matches Final Report Review) ── */}
        <div className="flex items-center justify-end gap-2.5 px-5 py-3.5 border-t border-slate-200 bg-slate-50">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="review-cancel-btn"
            className="px-4 py-2 text-[12.5px] font-semibold border border-slate-300 text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
          >Cancel</button>
          <button
            onClick={submitReview}
            disabled={!acknowledged || submitting}
            data-testid="review-confirm-btn"
            className="px-4 py-2 text-[12.5px] font-semibold text-white rounded-md transition-colors disabled:opacity-40 inline-flex items-center gap-1.5"
            style={{ background: '#004aad' }}
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            {submitting ? 'Confirming…' : 'Confirm Review'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ReviewTaxCertModal);
